var authenticationManager = require('authenticationManager');
var { Sentry, isEnabled: isSentryEnabled } = require('./sentry');
var env = require('env');
var distributionActions = require('distributionActions');
var soilDataActions = require('soilDataActions');
var cropDataActions = require('cropDataActions');
var fertilizerDataActions = require('fertilizerDataActions');
var farmingTypeActions = require('farmingTypeActions');
var userActions = require('userActions');
var {
  loadNotificationsInitial,
} = require('./managers/notificationsFeedThunks');
var { initMixpanel } = require('@managers/mixpanelManager');

// Watch for the auth state transitioning to logged-out (id_token cleared).
// On transition, hard-reload the app so any in-memory state from the prior
// session is dropped. The shared repo only clears tokens + dispatches
// logout_success; redirect is handled here, in the platform-specific layer.
const subscribeToAuthLogout = (store) => {
  let prevIdToken = store.getState()?.authentication_data?.tokens?.id_token;
  store.subscribe(() => {
    const nextIdToken = store.getState()?.authentication_data?.tokens?.id_token;
    if (prevIdToken && !nextIdToken) {
      setTimeout(() => window.location.reload(), 100);
    }
    prevIdToken = nextIdToken;
  });
};

export var init = (store) => {
  authenticationManager.init(store, env[env.stage].url);
  authenticationManager.initTokensSync(store);
  subscribeToAuthLogout(store);
  // Bind the shared mixpanelManager wrapper to the web SDK so that
  // getMixpanelInstance().register / track / identify forward straight to
  // window.mixpanel instead of disappearing into the queue. Must run
  // before any dispatch that may call into shared actions which use the
  // wrapper (e.g. distributionActions.selected_entities).
  initMixpanel(window.mixpanel);
  let userDetails = authenticationManager.getUserDetails();

  // Load all data once at app initialization instead of in each component.
  // The reference data below (soil, crop, user, notifications, fertilizer,
  // farming type) takes no distribution / farming unit / field as input, so it
  // has no dependency on the distribution tree — dispatch it in parallel with
  // load_distributions rather than gating it behind the tree load. Gating only
  // serialised independent work and left a window where the tree was painted
  // but field cards had no crop name/icon.
  if (userDetails != null) {
    store.dispatch(distributionActions.load_distributions(false, true));
    store.dispatch(soilDataActions.load_soil_data());
    store.dispatch(cropDataActions.load_crop_data());
    store.dispatch(userActions.get_user_data());
    store.dispatch(loadNotificationsInitial());
    store.dispatch(fertilizerDataActions.load_fertilizer_data());
    store.dispatch(farmingTypeActions.load_farming_type_data());
  }
  if (isSentryEnabled) {
    // Set structured user context for Sentry (init is done in sentry.js)
    if (userDetails) {
      Sentry.setUser({
        email: userDetails.email,
        username: userDetails.name,
      });
    }
    // Set additional context as tags (with null safety)
    var selectedFieldId =
      store.getState()?.distribution_data?.selected_entities?.field?.id;
    if (selectedFieldId) {
      Sentry.setTag('selectedField', selectedFieldId);
    }
  }

  if (['prod', 'stg'].includes(env.stage)) {
    // Smartlook identify user by email (only in prod and not on localhost)
    if (
      window.smartlook &&
      userDetails?.email &&
      env.stage === 'prod' &&
      !window.location.hostname.includes('localhost')
    ) {
      window.smartlook('identify', userDetails.email, {
        email: userDetails.email,
        name: userDetails.name, // if available
      });
    }

    // Register environment as a super property for all Mixpanel events
    mixpanel.register({
      Environment: env.stage,
    });
  } else {
    mixpanel.disable();
  }

  if (userDetails != null) {
    mixpanel.identify(userDetails.email);
    mixpanel.people.set({
      $last_login: new Date(),
      Units: userDetails.preferred_units,
    });
    mixpanel.track('Login', { Type: 'automatic' });
  }
};
